import { useMemo } from "react";
import { TrendingUp, TrendingDown, ExternalLink, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type {
  CashFlowRow,
  DisplayCurrency,
  ViewPeriod,
} from "@/hooks/finance/cash-flow/useCashFlow";
import { formatCurrency } from "@/lib/currency";

type Props = {
  rows: CashFlowRow[];
  periodKeys: string[];
  periodLabels: { key: string; label: string }[];
  displayCurrency: DisplayCurrency;
  isLoading: boolean;
  viewPeriod: ViewPeriod;
};

type Mover = {
  label: string;
  categoryId?: string;
  previous: number;
  current: number;
  delta: number;
  pct: number; // can be Infinity
};

function monthRange(monthKey: string): { from: string; to: string } {
  // monthKey = "yyyy-MM"
  const [y, m] = monthKey.split("-").map(Number);
  const from = `${monthKey}-01`;
  const end = new Date(Date.UTC(y, m, 0)); // last day of month
  const to = `${monthKey}-${String(end.getUTCDate()).padStart(2, "0")}`;
  return { from, to };
}

export function MoMMovers({
  rows,
  periodKeys,
  periodLabels,
  displayCurrency,
  isLoading,
  viewPeriod,
}: Props) {
  const movers = useMemo<Mover[]>(() => {
    if (viewPeriod !== "monthly" || periodKeys.length < 2) return [];
    const curKey = periodKeys[periodKeys.length - 1];
    const prevKey = periodKeys[periodKeys.length - 2];

    const expenseRows = rows.filter(
      (r) => r.key.includes("-exp-") && r.children && r.children.length > 0,
    );

    const list: Mover[] = [];
    for (const parent of expenseRows) {
      for (const child of parent.children!) {
        const current = child.values.get(curKey) ?? 0;
        const previous = child.values.get(prevKey) ?? 0;
        if (current === 0 && previous === 0) continue;
        const delta = current - previous;
        const pct =
          previous !== 0
            ? delta / Math.abs(previous)
            : current !== 0
              ? Infinity
              : 0;
        list.push({
          label: child.label,
          categoryId: child.categoryId,
          previous,
          current,
          delta,
          pct,
        });
      }
    }
    return list;
  }, [rows, periodKeys, viewPeriod]);

  if (viewPeriod !== "monthly") return null;
  if (isLoading) return <div className="h-48 rounded-xl bg-muted animate-pulse" />;
  if (periodKeys.length < 2 || movers.length === 0) return null;

  // Expense row values are stored as positive amounts (subtracted from sales
  // to compute net operating cash flow). A spike = spend increased in the
  // current period vs the previous => delta > 0. A drop = delta < 0.
  const spikes = movers
    .filter((m) => m.delta > 0)
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
    .slice(0, 8);
  const drops = movers
    .filter((m) => m.delta < 0)
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
    .slice(0, 8);

  const curLabel = periodLabels[periodLabels.length - 1]?.label ?? "";
  const prevLabel = periodLabels[periodLabels.length - 2]?.label ?? "";
  const curKey = periodKeys[periodKeys.length - 1];
  const { from: monthFrom, to: monthTo } = monthRange(curKey);

  const fmt = (v: number) => formatCurrency(Math.abs(v), displayCurrency);
  const fmtPct = (p: number) => {
    if (!isFinite(p)) return "new";
    return `${p > 0 ? "+" : ""}${(p * 100).toFixed(0)}%`;
  };

  const renderRow = (m: Mover, kind: "spike" | "drop") => {
    const tone =
      kind === "spike"
        ? "text-red-700 bg-red-50 border-red-200"
        : "text-emerald-700 bg-emerald-50 border-emerald-200";

    const labelNode = m.categoryId ? (
      <a
        href={`/finance/accounting/expenses?cat=${encodeURIComponent(
          m.categoryId,
        )}&from=${monthFrom}&to=${monthTo}`}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium truncate hover:underline inline-flex items-center gap-1"
      >
        {m.label}
        <ExternalLink className="h-3 w-3 opacity-60" />
      </a>
    ) : (
      <span className="font-medium truncate">{m.label}</span>
    );

    return (
      <div
        key={`${kind}-${m.label}`}
        className="flex items-center gap-2 text-xs py-1.5 border-b border-slate-100 last:border-b-0"
      >
        <div className="flex-1 min-w-0">
          <div className="truncate">{labelNode}</div>
          <div className="text-[10px] text-muted-foreground font-mono tabular-nums flex items-center gap-1 mt-0.5">
            <span>{fmt(m.previous)}</span>
            <ArrowRight className="h-2.5 w-2.5" />
            <span>{fmt(m.current)}</span>
          </div>
        </div>
        <div className="text-right whitespace-nowrap">
          <div className="font-mono font-semibold tabular-nums text-slate-800">
            {kind === "spike" ? "+" : "−"}
            {fmt(m.delta)}
          </div>
          <span
            className={`inline-block text-[9px] px-1.5 py-0.5 rounded-full border font-semibold ${tone}`}
          >
            {fmtPct(m.pct)}
          </span>
        </div>
      </div>
    );
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          Month-over-Month Movers
          <Badge variant="outline" className="text-[10px] font-normal">
            {curLabel} vs {prevLabel}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold text-red-700 uppercase tracking-wider">
            <TrendingUp className="h-3.5 w-3.5" />
            Spiked ({spikes.length})
          </div>
          {spikes.length === 0 ? (
            <p className="text-xs text-muted-foreground">No spend increases.</p>
          ) : (
            <div>{spikes.map((m) => renderRow(m, "spike"))}</div>
          )}
        </div>
        <div>
          <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold text-emerald-700 uppercase tracking-wider">
            <TrendingDown className="h-3.5 w-3.5" />
            Dropped ({drops.length})
          </div>
          {drops.length === 0 ? (
            <p className="text-xs text-muted-foreground">No spend reductions.</p>
          ) : (
            <div>{drops.map((m) => renderRow(m, "drop"))}</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
